/**
 * WHICH DAYS DOES THIS PERSON WORK — the one answer the calendars ask.
 *
 * ⚠️ THIS REPLACES `isWeekend()` FROM date-fns, WHICH IS HARDCODED TO SATURDAY AND
 * SUNDAY. Step 4, 07/08/2026. That assumption was invisible while every customer was
 * British and wrong the moment an office opened in Algiers, where the working week is
 * **Sunday to Thursday**. It appeared in nine places across four calendars.
 *
 * ⚠️ IT WAS NEVER ONLY SHADING. `isWeekend` was doing four different jobs, and one of
 * them changes what people can do:
 *   1. shading a cell grey,
 *   2. finding the next working day for "back at work",
 *   3. **deciding whether leave can be booked on that day** — so an Algerian employee
 *      could not book their working Sunday and COULD book their non-working Friday,
 *   4. hiding a bank holiday marker that fell on a "weekend".
 * Each one now asks whose week it is.
 *
 * ⚠️ THE FALLBACK LIVES HERE AND NOWHERE ELSE. The API sends `null` when it genuinely
 * cannot tell, and null becomes Monday-to-Friday **once, in this file**. Every other
 * copy of that fallback is the bug this workstream exists to remove — including one in
 * the payload, which is why the backend is careful to send null rather than guessing.
 *
 * ⚠️ NAMES, NOT NUMBERS, ACROSS THE WIRE. Python counts Monday=0; JavaScript's
 * `getDay()` counts Sunday=0. An integer crossing that boundary shifts everybody's week
 * by one day and still looks plausible. The API sends `["sunday", "monday", …]`.
 */

/** JavaScript's `getDay()` order — Sunday first. Do not reorder. */
const JS_DAY_NAMES = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

export const MONDAY_TO_FRIDAY = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday',
];

/**
 * Normalise whatever the API sent into a Set of day names.
 *
 * ⚠️ AN EMPTY LIST IS TREATED AS "UNKNOWN", NOT AS "WORKS NO DAYS". Somebody who works
 * no days at all would have every cell shaded and could book nothing — a broken screen
 * with no error. The backend rejects an empty working week at the door for the same
 * reason (`validate_working_hours`), so an empty list here means something upstream
 * went wrong, and Monday-to-Friday is the honest degradation.
 */
export const workingDaySet = (days) => {
  const list = Array.isArray(days) ? days.filter(Boolean) : [];
  // ⚠️ `.trim()` AS WELL AS `.toLowerCase()`. A check caught me on ' SUNDAY ': whitespace
  // survives lowercasing, and the whole point of sending names rather than numbers is
  // that they can't be quietly wrong. Same lesson as `holiday_applies` and its '   '.
  return new Set((list.length ? list : MONDAY_TO_FRIDAY)
    .map((d) => String(d).trim().toLowerCase()));
};

/**
 * Does this person work on this date?
 *
 * `days` is the list from the API (or null). Pass the same list every time for a given
 * person — building the Set per cell is cheap, but `workingDaySet` once per row is
 * cheaper on a wallchart with 30 columns.
 */
export const isWorkingDay = (date, days) =>
  workingDaySet(days).has(JS_DAY_NAMES[date.getDay()]);

/** The complement, because most call sites read better as "is this a day off". */
export const isNonWorkingDay = (date, days) => !isWorkingDay(date, days);

/**
 * The first day this person is actually back at work, on or after `date`.
 *
 * ⚠️ GUARDED AT SEVEN. If a pattern somehow says no days are worked this would loop for
 * ever, and a hung calendar is worse than a wrong date. `workingDaySet` should already
 * make that impossible; the guard is because "should already" has not been a reliable
 * statement in this codebase.
 */
export const nextWorkingDay = (date, days) => {
  const set = workingDaySet(days);
  const d = new Date(date.getTime());
  for (let i = 0; i < 7; i += 1) {
    if (set.has(JS_DAY_NAMES[d.getDay()])) return d;
    d.setDate(d.getDate() + 1);
  }
  return d;
};

/** "Mon to Fri", "Sun to Thu", "Mon, Wed and Fri" — for labels, never for logic. */
export const describeWorkingDays = (days) => {
  const set = workingDaySet(days);
  const SHORT = {
    monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
    friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
  };
  // Start the week on the first worked day so Sunday-to-Thursday reads as a run rather
  // than "Sun, Mon, Tue, Wed and Thu".
  const ordered = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const worked = ordered.filter((d) => set.has(d));
  if (!worked.length) return '';
  const start = ordered.indexOf(worked[0]) === 0 && set.has('sunday') && !set.has('saturday')
    ? ordered.indexOf('sunday') : ordered.indexOf(worked[0]);
  const rotated = [...ordered.slice(start), ...ordered.slice(0, start)];
  const positions = rotated.map((d, i) => (set.has(d) ? i : -1)).filter((i) => i >= 0);
  const contiguous = positions.every((p, i) => i === 0 || p === positions[i - 1] + 1);
  if (contiguous && worked.length > 2) {
    return `${SHORT[rotated[positions[0]]]} to ${SHORT[rotated[positions[positions.length - 1]]]}`;
  }
  const names = positions.map((p) => SHORT[rotated[p]]);
  return names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names[0];
};

/**
 * WHAT WOULD THIS PERSON FOLLOW IF THEY HAD NO PATTERN OF THEIR OWN?
 *
 * ⚠️ MIRRORS THE LOWER RUNGS OF `backend/services/schedule.effective_pattern` — their
 * location's week, else the company's — and mirrors nothing else. The top rung (the
 * person's own dated pattern) is deliberately absent: this answers the question the
 * editor asks while the form is still unsaved, which the server cannot answer because
 * the admin may have just changed the location dropdown.
 *
 * ⚠️ IT IS A PREVIEW, NEVER AN INPUT TO ANYBODY'S LEAVE. The server resolves the real
 * chain when it saves and when it prices. Same discipline as `lib/companyHolidays.js`:
 * a mirror that is allowed to exist because the alternative — the screen not knowing
 * what it is about to inherit — is what made an admin think they had set a pattern when
 * they hadn't.
 *
 * Returns the pattern object, or null when there is nothing to inherit.
 */
export const inheritedWeek = (location, companyHours) => {
  const parse = (raw) => {
    if (!raw) return null;
    try {
      const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
      // An object with no worked day is not a usable week — treat as nothing, exactly
      // as `weekly_minutes(...)` being zero makes the backend fall to the next rung.
      // ⚠️ BOTH SHAPES (BUGS.md F89, 03/11/2026): a day-level `enabled` is the company's older
      // business-hours shape — reading only am/pm made "Different for this person" do nothing.
      const worked = Object.keys(p || {}).filter(
        (d) => p[d]?.enabled === true || ['am', 'pm'].some((s) => p[d]?.[s]?.enabled));
      return worked.length ? p : null;
    } catch (e) {
      return null;
    }
  };
  return parse(location?.working_hours) || parse(companyHours) || null;
};

/** "Draria Branch" / "the company" — whose week they are following, for the label. */
export const inheritedFrom = (location, companyHours) => {
  const has = (raw) => !!inheritedWeek({ working_hours: raw }, null);
  if (location && has(location.working_hours)) return location.name || 'their location';
  if (has(companyHours)) return 'the company';
  return 'the company';
};

/**
 * What to say after a save that may have re-priced people.
 *
 * ⚠️ IT CLOSES THE SAME LOOP THE LOCATION TOAST DOES. Rob, 07/08/2026: a company setting
 * moves EVERYBODY's entitlement, and until now nothing recalculated them — the app just
 * put a note on the balance telling an admin to go and re-save employees one at a time.
 * Now it does the work, and says so: a change that silently moves 200 people's leave is
 * exactly the kind of thing to tell someone about at the moment they cause it.
 *
 * ⚠️ "RECHECKED", NOT "CHANGED" — same reason as `repricedMessage`. Everyone affected is
 * recomputed; most of them won't move.
 */
export const settingsSavedMessage = (what, count, inBackground) => {
  const base = what || 'Settings saved';
  if (inBackground) return `${base} — leave balances are being rechecked in the background`;
  const n = Number(count) || 0;
  if (n <= 0) return base;
  return `${base} — leave rechecked for ${n} ${n === 1 ? 'person' : 'people'}`;
};
